import React, { useState, useEffect } from 'react';
import { Modal, Select, Spin, message, Input } from 'antd';
import { useFormContext, Controller } from 'react-hook-form';
import { AlertSuccess } from '../../../../util/Alert';
import { useQueryClient } from '@tanstack/react-query';
import fitMissionServices from '../../../../../services/fit-mission.services';

const { Option } = Select;

const LegacyUserRewardModal = ({
  open,
  handleClose,
  memberCode,
  missionUser,
  month,
  year,
  onSuccess,
}) => {
  const { watch, handleSubmit, setValue, control } = useFormContext();
  const queryClient = useQueryClient();
  const [selectedReward, setSelectedReward] = useState(null);
  const [rewardOptions, setRewardOptions] = useState([]);
  const [rewardList, setRewardList] = useState([]);

  // Get available rewards
  const { isFetching: isFetchingRewards } =
    fitMissionServices.useQueryGetMissionRewards(
      { take: 100, page: 1, inStock: true },
      (data) => {
        if (data?.data) {
          setRewardList(data.data);
          setRewardOptions(
            data.data.map((reward) => ({
              value: reward.id,
              label: `${reward.title} (ใช้ ${reward.point} คะแนน) - คงเหลือ ${reward.stockQuantity} ชิ้น`,
            })),
          );
        }
      },
    );

  // Create mutation
  const { mutate: createUserReward, isLoading: isCreating } =
    fitMissionServices.useMutationCreateMissionUserReward(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-mission-point-info-by-mission-user-id'],
        refetchType: 'all',
      });
      AlertSuccess('แลกรางวัลสำเร็จ');
      onSuccess();
      handleClose();
    });

  const _HandleSubmit = handleSubmit((data) => {
    if (!data.missionRewardId) {
      message.error('กรุณาเลือกรางวัล');
      return;
    }

    const reward = rewardList.find((r) => r.id === data.missionRewardId);
    const quantity = Number(data.quantity) || 1;

    const payload = {
      missionRewardId: data.missionRewardId,
      missionUserId: missionUser?.id,
      quantity,
      title: reward?.title,
      description: reward?.description,
      image: reward?.image,
    };

    createUserReward(payload);
  });

  const handleRewardChange = (rewardId) => {
    setValue('missionRewardId', rewardId);
    const reward = rewardList.find((r) => r.id === rewardId);
    setSelectedReward(reward);
    setValue('quantity', 1);
  };

  useEffect(() => {
    if (open && missionUser) {
      setValue('missionUserId', missionUser.id);
    }
  }, [open, missionUser, setValue]);

  return (
    <Modal
      title="แลกรางวัล"
      visible={open}
      onCancel={handleClose}
      okText="แลกรางวัล"
      cancelText="ยกเลิก"
      onOk={_HandleSubmit}
      confirmLoading={isCreating}
      width={500}
    >
      <Spin spinning={isFetchingRewards}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            รหัสสมาชิก: <span style={{ color: '#1890ff' }}>{memberCode}</span>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>เลือกสินค้าแลก *</strong>
          </div>
          <Controller
            name="missionRewardId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                style={{ width: '100%' }}
                placeholder="เลือกสินค้าแลก"
                onChange={handleRewardChange}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {rewardOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            )}
          />
        </div>

        {selectedReward && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: '#f5f5f5',
              borderRadius: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {selectedReward.image && (
                <img
                  src={selectedReward.image}
                  alt={selectedReward.title}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 8,
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  {selectedReward.title}
                </div>
                <div style={{ color: '#1890ff', fontWeight: 'bold' }}>
                  {selectedReward.point} คะแนน
                </div>
                <div style={{ color: '#666', fontSize: 12 }}>
                  คงเหลือ: {selectedReward.stockQuantity} ชิ้น
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>จำนวน</strong>
          </div>
          <Controller
            name="quantity"
            control={control}
            rules={{
              required: 'กรุณาระบุจำนวน',
              max: {
                value: selectedReward?.stockQuantity || 0,
                message: `จำนวนไม่เกิน ${selectedReward?.stockQuantity || 0} ชิ้น`,
              },
              min: {
                value: 1,
                message: 'จำนวนขั้นต่ำ 1',
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <Input
                  value={value}
                  min={1}
                  max={selectedReward?.stockQuantity || 0}
                  style={{ width: '100%' }}
                  placeholder="จำนวน"
                  status={error ? 'error' : undefined}
                  onChange={(e) => {
                    const cleaned =
                      typeof e.target.value === 'string'
                        ? e.target.value.replace(/[^\d]/g, '')
                        : e.target.value;

                    const num = Number(cleaned);
                    const maxQty = selectedReward?.stockQuantity || 0;
                    if (!cleaned) {
                      onChange('');
                    } else if (num > maxQty) {
                      onChange(maxQty);
                    } else if (num < 1) {
                      onChange(1);
                    } else {
                      onChange(num);
                    }
                  }}
                />
                {error && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                    {error.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        {selectedReward && (
          <div
            style={{
              padding: 12,
              background: '#fff7e6',
              borderRadius: 8,
              border: '1px solid #ffd591',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>รวมคะแนนที่ใช้แลก:</span>
              <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>
                {(selectedReward?.point || 0) * (watch('quantity') || 1)} คะแนน
              </span>
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default LegacyUserRewardModal;
